<template>
    <page-loading v-if="loading || !form"></page-loading>
    <form-container v-else>
        <page-header class="mb-6 lg:mb-12">
            <template v-slot:subtitle>
                <router-link :to="{ name: 'campaigns' }" class="no-underline! text-sky-600! text-base font-normal">
                    <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                    All Campaigns
                </router-link>
            </template>

            <slot name="header">{{ pageTitle }}</slot>
        </page-header>

        <div class="grid grid-cols-12">
            <div class="col-span-12 lg:col-span-2 flex flex-col w-full pb-8 mt-2">
                <span class="text-base text-slate-700 font-extrabold px-2 py-1">Details</span>
                <ul class="border-l border-slate-400 border-solid border-0 flex flex-col items-start gap-1 ml-2">
                    <li
                        v-for="item in menuItems"
                        :key="item.id"
                        @click="focusSection(item.id)"
                        class="text-sm cursor-pointer"
                    >
                        <div class="flex items-center gap-2">
                            <div
                                class="w-[3px] h-[25px] bg-slate-400 border-0 rounded-r-xl"
                                :class="{
                                    invisible: selected !== item.id,
                                }"
                            ></div>
                            <span
                                :class="{
                                    'text-sky-700': selected === item.id,
                                    'text-slate-600': selected !== item.id,
                                }"
                            >
                                {{ item.label }}</span
                            >
                        </div>
                    </li>
                </ul>
            </div>
            <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-sm)">
                <quick-blast-group-status-alert
                    v-if="!currentGroupCanSend"
                    class="mb-6"
                    :status="currentGroup?.status"
                ></quick-blast-group-status-alert>
                <page-header>
                    <div class="flex items-center gap-6">
                        Details
                        <vx-badge :color="CAMPAIGN_STATUSES[form.status]?.badge" size="lg" borders>{{
                            CAMPAIGN_STATUSES[form.status]?.label
                        }}</vx-badge>
                    </div>
                </page-header>
                <div class="flex flex-col gap-8">
                    <group-quick-blast-edit-name id="name" v-model="form.name" @click="selected = 'name'" />
                    <resource-category
                        id="category"
                        v-model="form.category"
                        @click="selected = 'category'"
                        @reset="form.category = null"
                    >
                        <template v-slot:headerSubtitle>
                            Optionally select the category that best describes this resource.
                        </template>
                    </resource-category>
                    <group-quick-blast-edit-type
                        id="type"
                        :modelValue="form.type"
                        @update:modelValue="changeCampaignType($event)"
                        @click="selected = 'type'"
                    />
                    <group-quick-blast-edit-from
                        v-if="currentOrgUnit?.id"
                        id="from"
                        v-model="form.groups"
                        :orgUnitId="currentOrgUnit.id"
                        @click="selected = 'from'"
                    />
                    <group-quick-blast-edit-to
                        id="to"
                        @click="selected = 'to'"
                        v-model="form.audiences"
                        :campaignType="form.type"
                        :groupIds="form.groups?.map((g) => g.group_id)"
                    />
                    <group-quick-blast-edit-what
                        id="what"
                        @click="selected = 'what'"
                        v-model="form.campaign_messages[0]"
                        :campaignType="form.type"
                        :errors="bodyErrors"
                        @snippetUsed="snippetUsed = $event"
                    />
                    <group-quick-blast-edit-when
                        id="when"
                        @click="selected = 'when'"
                        v-model="form.live_at"
                        :campaignType="form.type"
                    />

                    <vx-sticky-footer>
                        <router-button :to="{ name: 'campaigns' }" class="w-20" size="lg" color="muted">
                            Cancel
                        </router-button>
                        <vx-button
                            @click="submit()"
                            data-test="submit-button"
                            class="grow"
                            size="lg"
                            color="primary"
                            :loading="processing"
                            :disabled="!canSave || !currentGroupCanSend"
                        >
                            {{ queryParams.duplicate_from_id ? 'Create' : 'Update' }}
                        </vx-button>
                    </vx-sticky-footer>
                </div>
            </div>
        </div>
    </form-container>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import FormContainer from '~/components/general/FormContainer.vue';
import { actionsClient, campaignsClient } from '~/services';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowLeft, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxBadge, VxStickyFooter, VxSwal } from '@voxie/frontend-components';
import GroupQuickBlastEditName from './GroupQuickBlastEditName.vue';
import ResourceCategory from '~/components/general/categories/ResourceCategory.vue';
import GroupQuickBlastEditType from './GroupQuickBlastEditType.vue';
import GroupQuickBlastEditFrom from './GroupQuickBlastEditFrom.vue';
import GroupQuickBlastEditTo from './GroupQuickBlastEditTo.vue';
import GroupQuickBlastEditWhat from './GroupQuickBlastEditWhat.vue';
import GroupQuickBlastEditWhen from './GroupQuickBlastEditWhen.vue';
import QuickBlastGroupStatusAlert from '~/components/quick-blast/QuickBlastGroupStatusAlert.vue';
import dayjs, { toUTCDateTimeFormat } from '~/utils/dayjs';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { useUrlSearchParams } from '@vueuse/core';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { CAMPAIGN_STATUSES, buildCampaignMessage, extractCampaignMessageErrors } from '~/constants/campaign';
import { useRouter } from 'vue-router';
import RouterButton from '~/components/general/RouterButton.vue';
import { HTTP_NOT_FOUND, HTTP_UNPROCESSABLE_ENTITY } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import { useValidateMessageStandards } from '~/components/quick-blast/useValidateMessageStandards';
import { catchRequestError } from '~/utils/request';
import { escapeHtml } from '~/utils/html';
import { useSendingCalendar } from '~/components/quick-blast/useSendingCalendar';
import { ValidationError } from '~/utils/errors';

const teamId = Spark.state.currentTeam.id;

const { getMessageRules } = useMessageRules();
const { isDateTimeAllowed, clearData: clearSendingCalendar } = useSendingCalendar();
clearSendingCalendar();

const props = defineProps({
    campaignId: {
        type: Number,
    },
});

const router = useRouter();
const toasted = useToasted();
const queryParams = useUrlSearchParams();
const { currentGroup, currentGroupCanSend, currentOrgUnit } = useCurrentGroup();
const { lockedSnippetsEnabled } = useMessageRules();

const loading = ref(false);

const form = ref();

const originalCampaignData = ref({
    body: '',
    media_url: null,
    media_content_type: null,
    live_at: null,
    snippet_id: null,
    field_bindings: [],
});

const messageWasEdited = computed(() => {
    const currentMessage = form.value?.campaign_messages?.[0];
    if (!currentMessage) {
        return false;
    }

    return (
        currentMessage.body !== originalCampaignData.value.body ||
        currentMessage.media_url !== originalCampaignData.value.media_url ||
        currentMessage.media_content_type !== originalCampaignData.value.media_content_type ||
        haveBindingsChanged(currentMessage.field_bindings || [])
    );
});

const haveBindingsChanged = (currentBindings) => {
    const originalBindings = originalCampaignData.value.field_bindings || [];

    for (const binding of currentBindings) {
        if (binding.value === null && !originalBindings.some((b) => b.key === binding.key)) {
            continue;
        }

        const originalValue = originalBindings.find((b) => b.key === binding.key)?.value;

        if (originalValue !== binding.value) {
            return true;
        }
    }

    return false;
};

const dirty = ref(false);
watch(
    () => form.value,
    () => {
        dirty.value = true;
    },
    {
        deep: true,
    }
);

const canSave = computed(() => {
    return dirty.value;
});

// When editing campaign name a main page title won't be changed reactively.
const pageTitle = ref();

const menuItems = computed(() => {
    const items = [
        {
            id: 'name',
            label: 'Name',
        },
        {
            id: 'category',
            label: 'Category',
        },
        {
            id: 'type',
            label: 'Type',
        },
    ];

    if (currentOrgUnit.value?.id) {
        items.push({
            id: 'from',
            label: 'From',
        });
    }

    items.push(
        {
            id: 'to',
            label: 'To',
        },
        {
            id: 'what',
            label: 'What',
        },
        {
            id: 'when',
            label: 'When',
        }
    );

    return items;
});

const selected = ref('name');

const focusSection = (id) => {
    selected.value = id;

    document.getElementById(id)?.scrollIntoView();
};

const loadCampaign = async (id) => {
    if (loading.value) {
        return;
    }

    loading.value = true;

    try {
        const response = currentOrgUnit.value?.id
            ? await campaignsClient.orgUnitCampaignGet(teamId, currentOrgUnit.value.id, id)
            : await campaignsClient.groupCampaignGet(teamId, currentGroup.value.group_id, id);

        if (!queryParams.duplicate_from_id && !['draft', 'scheduled'].includes(response.data.status)) {
            toasted.error('Campaign must be in draft or scheduled status to edit.');
            router.replace({ name: 'campaigns' });
            return;
        }

        const segments = (response.data.segments || []).map((segment) => ({
            ...segment,
            label: segment.name,
            sourceType: 'segments',
        }));

        const externalLists = (response.data.external_contact_lists || []).map((list) => ({
            ...list,
            label: list.name,
            sourceType: 'external-contact-lists',
        }));

        form.value = {
            ...response.data,
            live_at: response.data.live_at ? dayjs.utc(response.data.live_at).toDate() : null,
            campaign_messages: response.data.campaign_messages.length
                ? response.data.campaign_messages
                : [
                      {
                          body: '',
                          media_url: null,
                          media_content_type: null,
                          status: 'published',
                      },
                  ],
            audiences: [...segments, ...externalLists],
            groups: response.data.groups || [],
        };

        originalCampaignData.value = {
            body: form.value.campaign_messages[0].body,
            media_url: form.value.campaign_messages[0].media_url,
            media_content_type: form.value.campaign_messages[0].media_content_type,
            live_at: form.value.live_at,
            snippet_id: form.value.snippet_id,
            field_bindings: form.value.campaign_messages[0].field_bindings,
        };

        if (queryParams.duplicate_from_id) {
            form.value.id = undefined;
            form.value.name = '';
            form.value.live_at = null;
            form.value.status = 'draft';
        }

        pageTitle.value = queryParams.duplicate_from_id ? `Duplicating: ${response.data.name}` : response.data.name;

        await getMessageRules({ groupId: currentGroup.value?.group_id, orgUnitId: currentOrgUnit.value?.id });

        if (
            onlyLockedSnippetsAllowed.value &&
            !form.value.campaign_messages[0].snippet_id &&
            queryParams.duplicate_from_id
        ) {
            form.value.campaign_messages[0].body = '';
            form.value.campaign_messages[0].media_url = '';
            form.value.campaign_messages[0].media_content_type = '';
        }

        loading.value = false;
    } catch (error) {
        if (error?.response?.status === HTTP_NOT_FOUND) {
            router.replace({ name: 'campaigns' });
            return;
        }

        toasted.error(catchRequestError(error));
    } finally {
        dirty.value = false;
    }
};

onMounted(() => {
    if (props.campaignId) {
        loadCampaign(props.campaignId);
    }

    if (queryParams.duplicate_from_id) {
        loadCampaign(queryParams.duplicate_from_id);
    }
});

const snippetUsed = ref();
const vettingBlocked = ref(false);

const {
    bodyErrors,
    validate: validateAiVetting,
    clearErrors,
} = useValidateMessageStandards({
    campaignType: () => form.value?.type,
    messageBody: () => form.value?.campaign_messages?.[0]?.body || '',
    messageMediaUrl: () => form.value?.campaign_messages?.[0]?.media_url || null,
    messageMediaContentType: () => form.value?.campaign_messages?.[0]?.media_content_type || null,
    messageSnippetId: () => form.value?.campaign_messages?.[0]?.snippet_id || null,
    messageSnippetFieldBindings: () => form.value?.campaign_messages?.[0]?.field_bindings || [],
    snippetIsLocked: () => Boolean(snippetUsed.value?.is_locked),
    audienceSegmentIds: () => form.value?.segments?.map((s) => s.id) || [],
    audienceExternalContactListIds: () => [],
    audienceGroupIds: () => form.value?.groups?.map((g) => g.group_id) || [],
});

const onlyLockedSnippetsAllowed = computed(() => lockedSnippetsEnabled(form.value.type));

const saveData = async () => {
    clearErrors();
    vettingBlocked.value = false;

    if (messageWasEdited.value) {
        const validationPassed = await validateAiVetting();
        if (!validationPassed) {
            vettingBlocked.value = true;
            return;
        }
    }

    if (!form.value.audiences || form.value.audiences.length === 0) {
        throw new ValidationError('Please select at least one audience before saving.');
    }

    if (
        form.value.status === 'draft' &&
        form.value.live_at &&
        form.value.live_at !== 'now' &&
        dayjs(form.value.live_at).isBefore(dayjs())
    ) {
        form.value.live_at = 'now';
    }

    const segments = form.value.audiences.filter((a) => a.sourceType === 'segments');
    const externalLists = form.value.audiences.filter((a) => a.sourceType === 'external-contact-lists');

    const payload = {
        ...form.value,
    };

    delete payload.segment_ids;
    delete payload.external_contact_list_ids;
    delete payload.segments;
    delete payload.external_contact_lists;
    delete payload.audiences;
    delete payload.groups;

    // Always send both fields explicitly for independent field handling
    // Empty array = clear field, omitting field = keep unchanged
    payload.segment_ids = segments.map((segment) => segment.id);
    payload.external_contact_list_ids = externalLists.map((list) => list.id);

    payload.group_ids = form.value.groups.map((group) => group.group_id);

    if (props.campaignId && !messageWasEdited.value) {
        payload.campaign_messages = undefined;
    } else if (payload.campaign_messages.length) {
        const message = form.value.campaign_messages[0];

        payload.campaign_messages = [
            buildCampaignMessage({
                snippetId: message.snippet_id,
                snippetIsLocked: Boolean(snippetUsed.value?.is_locked),
                fieldBindings: message.field_bindings || [],
                body: message.body,
                mediaUrl: message.media_url,
                mediaContentType: message.media_content_type,
                status: message.status,
            }),
        ];
    }

    const preparedLiveAt = form.value.live_at === 'now' ? dayjs() : dayjs(form.value.live_at);

    if (form.value.live_at !== originalCampaignData.value.live_at) {
        if (!isDateTimeAllowed(preparedLiveAt)) {
            throw new ValidationError(
                'The selected date and time are unavailable. Please choose a permitted date and time.'
            );
        }

        payload.live_at = form.value.live_at ? toUTCDateTimeFormat(preparedLiveAt) : null;
    } else if (form.value.live_at) {
        delete payload.live_at;
    }

    try {
        const response = currentOrgUnit.value?.id
            ? await campaignsClient.orgUnitCampaignSave(teamId, currentOrgUnit.value?.id, payload)
            : await campaignsClient.groupCampaignSave(teamId, currentGroup.value.group_id, payload);
        form.value.id = response.data?.id;

        if (form.value.live_at && form.value.live_at !== originalCampaignData.value.live_at) {
            const status = form.value.live_at === 'now' ? 'published' : 'scheduled';

            if (currentOrgUnit.value?.id) {
                await campaignsClient.orgUnitCampaignChangeStatus(
                    teamId,
                    currentOrgUnit.value?.id,
                    response.data?.id,
                    status
                );
            } else {
                await campaignsClient.groupCampaignChangeStatus(
                    teamId,
                    currentGroup.value.group_id,
                    response.data?.id,
                    status
                );
            }

            form.value.status = status;
        }

        if (
            form.value.campaign_messages[0].snippet_id &&
            form.value.campaign_messages[0].snippet_id !== originalCampaignData.value.snippet_id &&
            (snippetUsed.value?.is_locked || form.value.campaign_messages[0].body.includes(snippetUsed.value.body))
        ) {
            actionsClient.send('snippet_used');
        }

        originalCampaignData.value = {
            body: form.value.campaign_messages[0].body,
            media_url: form.value.campaign_messages[0].media_url,
            media_content_type: form.value.campaign_messages[0].media_content_type,
            live_at: form.value.live_at,
            snippet_id: form.value.snippet_id,
            field_bindings: form.value.campaign_messages[0].field_bindings,
        };
    } catch (error) {
        if (error?.response?.status === HTTP_UNPROCESSABLE_ENTITY) {
            const errors = extractCampaignMessageErrors(error);
            if (errors.length > 0) {
                bodyErrors.value = errors;
            }
        }

        // used by `useFormSubmission` error handling
        throw error;
    }
};

const resetForm = () => {
    if (!vettingBlocked.value && (!props.campaignId || queryParams.duplicate_from_id)) {
        form.value = {
            ...form.value,
            id: undefined,
            name: '',
            live_at: null,
            status: 'draft',
        };
    }
};

const changeCampaignType = (campaignType) => {
    const previousCampaignType = form.value.type;

    // when changing the type we need to clear out the message data when using locked snippets
    if (!lockedSnippetsEnabled('marketing')) {
        // do nothing if not enabled
        form.value.type = campaignType;
        return;
    }

    if (campaignType !== previousCampaignType) {
        VxSwal.confirmation(
            'Changing Type',
            `Changing the type while on <strong>Locked Snippets</strong> will make you lose your message. Do you want to change the type from <strong>${previousCampaignType}</strong> to <strong>${campaignType}</strong>?`,
            'danger'
        ).then((confirmed) => {
            if (!confirmed) {
                return;
            }

            form.value.type = campaignType;
            form.value.campaign_messages[0].body = '';
            form.value.campaign_messages[0].media_url = undefined;
            form.value.campaign_messages[0].media_content_type = undefined;
            form.value.campaign_messages[0].snippet_id = undefined;
            form.value.campaign_messages[0].field_bindings = [];
        });
    }
};

const { submit, processing } = useFormSubmission({
    data: form,
    preserveScroll: () => Boolean(vettingBlocked.value || props.campaignId || !queryParams.duplicate_from_id),
    submit: saveData,
    reset: resetForm,
    notification: () => {
        if (vettingBlocked.value) {
            return undefined;
        }

        if (form.value.status === 'published') {
            return {
                message: `Campaign <strong>${escapeHtml(form.value.name)}</strong> is live`,
                vueRedirect: { name: 'campaigns' },
            };
        }

        if (queryParams.duplicate_from_id) {
            return {
                message: `Campaign ${escapeHtml(form.value.name)} created`,
                vueRedirect: { name: 'campaigns' },
            };
        }

        return {
            icon: faSquareArrowUpLeft,
            message: `Campaign updated`,
            action: {
                label: 'Back to Campaigns',
                onClick: () => router.push({ name: 'campaigns' }),
            },
        };
    },
});
</script>
